import { Stack } from '@mui/material';
import { useCallback } from 'react';
import { Tab } from '../types';
import { BaseDecarbTab } from './DecarbTab';

type Props = {
  currentTab: Tab;
  onClick: (nextTab: Tab) => void;
  // `cost` is separately optional: a tab can have a known energy demand but no
  // trustworthy cost figure yet (an HVAC alternative whose passive charge the
  // backend has not recomputed since the last edit).
  conventionalMetrics?: { cost?: number; energyDemand: number };
  reducedCarbonMetrics?: { cost?: number; energyDemand: number };
  zeroCarbonMetrics?: { cost?: number; energyDemand: number };
};

export const DecarbTabsPanel: React.FC<Props> = ({
  onClick,
  currentTab,
  conventionalMetrics,
  reducedCarbonMetrics,
  zeroCarbonMetrics,
}) => {
  const onClickConventional = useCallback(
    () => onClick('conventionalAlternative'),
    [onClick],
  );
  const onClickReduced = useCallback(
    () => onClick('reducedCarbonAlternative'),
    [onClick],
  );
  const onClickZero = useCallback(
    () => onClick('zeroCarbonAlternative'),
    [onClick],
  );

  return (
    <Stack
      direction={'row'}
      width={'100%'}
      height={{ sm: 60, lg: 65, xl: 70, xxl: 75 }}
      flexShrink={0}
      justifyContent={'space-between'}
      spacing={{ md: 12, lg: 20 }}
      marginY={2}
      position={'relative'}
      zIndex={0}
      // this is the line between each tab
      sx={{
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: 'success.main',
          zIndex: 0,
        },
        // this tells all tabs to go 'above' the above line
        '& > *': {
          position: 'relative',
          zIndex: 1,
        },
        // this tells the first tab to not render an 'arrow' to the left of it
        '& > :first-of-type .MuiPaper-root::after': {
          display: 'none',
        },
      }}
    >
      <BaseDecarbTab
        onClick={onClickConventional}
        tab={'conventionalAlternative'}
        selected={currentTab === 'conventionalAlternative'}
        costAndEnergyMetrics={conventionalMetrics}
      />
      <BaseDecarbTab
        selected={currentTab === 'reducedCarbonAlternative'}
        onClick={onClickReduced}
        tab={'reducedCarbonAlternative'}
        costAndEnergyMetrics={reducedCarbonMetrics}
        conventionalCostAndEnergyMetrics={conventionalMetrics}
      />
      <BaseDecarbTab
        selected={currentTab === 'zeroCarbonAlternative'}
        onClick={onClickZero}
        tab={'zeroCarbonAlternative'}
        costAndEnergyMetrics={zeroCarbonMetrics}
        conventionalCostAndEnergyMetrics={conventionalMetrics}
      />
    </Stack>
  );
};
